import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router';
import {
  createCountry,
  createNewsCategory,
  createNewsTag,
  createEventType,
  createRegion,
  createRouteCategory,
  createRouteDifficulty,
  createRouteTag,
  createCounty,
  createLocationBestSeason,
  createLocationCategory,
  createLocationDifficulty,
  createLocationRoadType,
  createSettlement,
  createTag,
  getDictionaryItem,
  getApiErrorMessage,
  listCounties,
  listCountries,
  listRegions,
  patchDictionaryItem,
  type LocationDictionaryItem,
  type DictionaryGroup,
} from '../lib/api';
import { ADD_DICTIONARY_TEMPLATE } from '../templates/addDictionaryTemplate';

type ModuleKey = 'locations' | 'routes' | 'news' | 'events';
type DictionaryGroupKey = DictionaryGroup;

const CYRILLIC_MAP: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'yo',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

const transliterate = (value: string): string =>
  value
    .split('')
    .map((char) => CYRILLIC_MAP[char.toLowerCase()] ?? char)
    .join('');

const slugify = (value: string): string =>
  transliterate(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const GROUP_KEYS: DictionaryGroupKey[] = [
  'countries',
  'regions',
  'counties',
  'settlements',
  'tags',
  'location-categories',
  'location-road-types',
  'location-difficulties',
  'location-best-seasons',
  'route-categories',
  'route-difficulties',
  'route-tags',
  'news-categories',
  'news-tags',
  'event-types',
];

const GROUP_MODULE_MAP: Record<DictionaryGroupKey, ModuleKey> = {
  countries: 'locations',
  regions: 'locations',
  counties: 'locations',
  settlements: 'locations',
  tags: 'locations',
  'location-categories': 'locations',
  'location-road-types': 'locations',
  'location-difficulties': 'locations',
  'location-best-seasons': 'locations',
  'route-categories': 'routes',
  'route-difficulties': 'routes',
  'route-tags': 'routes',
  'news-categories': 'news',
  'news-tags': 'news',
  'event-types': 'events',
};

const MODULE_GROUP_OPTIONS: Record<ModuleKey, Array<{ value: DictionaryGroupKey; label: string }>> = {
  locations: [
    { value: 'countries', label: 'Countries' },
    { value: 'regions', label: 'Regions' },
    { value: 'counties', label: 'Counties' },
    { value: 'settlements', label: 'Settlements' },
    { value: 'tags', label: 'Tags' },
    { value: 'location-categories', label: 'Categories' },
    { value: 'location-road-types', label: 'Road Types' },
    { value: 'location-difficulties', label: 'Difficulties' },
    { value: 'location-best-seasons', label: 'Best Seasons' },
  ],
  routes: [
    { value: 'route-categories', label: 'Route Categories' },
    { value: 'route-difficulties', label: 'Route Difficulties' },
    { value: 'route-tags', label: 'Route Tags' },
  ],
  news: [
    { value: 'news-categories', label: 'News Categories' },
    { value: 'news-tags', label: 'News Tags' },
  ],
  events: [{ value: 'event-types', label: 'Event Types' }],
};

export default function AddDictionaryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => ADD_DICTIONARY_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const alertBox = root.querySelector<HTMLElement>('#add-dictionary-alert');
    const alertIcon = root.querySelector<HTMLElement>('#add-dictionary-alert-icon');
    const alertTitle = root.querySelector<HTMLElement>('#add-dictionary-alert-title');
    const alertText = root.querySelector<HTMLElement>('#add-dictionary-alert-text');
    const pageTitle = root.querySelector<HTMLElement>('#add-dictionary-page-title');

    const form = root.querySelector<HTMLFormElement>('#add-dictionary-form');
    const nameInput = root.querySelector<HTMLInputElement>('#entry-name');
    const slugInput = root.querySelector<HTMLInputElement>('#entry-slug');
    const typeSelect = root.querySelector<HTMLSelectElement>('#entry-type');
    const moduleSelect = root.querySelector<HTMLSelectElement>('#entry-module');
    const orderInput = root.querySelector<HTMLInputElement>('#entry-order');
    const activeInput = root.querySelector<HTMLInputElement>('#entry-active');

    const countryWrap = root.querySelector<HTMLElement>('#entry-parent-country-wrap');
    const regionWrap = root.querySelector<HTMLElement>('#entry-parent-region-wrap');
    const countyWrap = root.querySelector<HTMLElement>('#entry-parent-county-wrap');
    const countrySelect = root.querySelector<HTMLSelectElement>('#entry-parent-country');
    const regionSelect = root.querySelector<HTMLSelectElement>('#entry-parent-region');
    const countySelect = root.querySelector<HTMLSelectElement>('#entry-parent-county');

    const saveBtn = root.querySelector<HTMLButtonElement>('#add-dictionary-save-btn');
    const saveNextBtn = root.querySelector<HTMLButtonElement>('#add-dictionary-save-next-btn');
    const saveBtnLabel = root.querySelector<HTMLElement>('#add-dictionary-save-btn-label');

    if (
      !alertBox ||
      !alertIcon ||
      !alertTitle ||
      !alertText ||
      !form ||
      !nameInput ||
      !slugInput ||
      !typeSelect ||
      !moduleSelect ||
      !orderInput ||
      !activeInput ||
      !countryWrap ||
      !regionWrap ||
      !countyWrap ||
      !countrySelect ||
      !regionSelect ||
      !countySelect ||
      !saveBtn ||
      !saveNextBtn ||
      !saveBtnLabel ||
      !pageTitle
    ) {
      return;
    }

    const editIdParam = searchParams.get('id');
    const editId = editIdParam ? Number(editIdParam) : NaN;
    const isEditMode = Number.isFinite(editId) && editId > 0;

    let disposed = false;
    let slugManuallyEdited = false;
    let countries: LocationDictionaryItem[] = [];
    let regions: LocationDictionaryItem[] = [];
    let counties: LocationDictionaryItem[] = [];
    let locationParentsLoaded = false;

    const rebuildTypeOptions = (module: ModuleKey, selected?: DictionaryGroupKey) => {
      const options = MODULE_GROUP_OPTIONS[module];
      typeSelect.innerHTML = options
        .map((option) => `<option value="${option.value}">${option.label}</option>`)
        .join('');
      const nextValue = selected && options.some((option) => option.value === selected) ? selected : options[0].value;
      typeSelect.value = nextValue;
      moduleSelect.value = module;
    };

    const showAlert = (kind: 'success' | 'error' | 'info', title: string, text: string) => {
      alertBox.classList.remove('hidden', 'bg-emerald-50', 'border-emerald-200', 'bg-rose-50', 'border-rose-200', 'bg-blue-50', 'border-blue-200');
      alertBox.classList.add('flex');

      if (kind === 'success') {
        alertBox.classList.add('bg-emerald-50', 'border-emerald-200');
        alertIcon.className = 'material-symbols-outlined text-emerald-600';
        alertIcon.textContent = 'check_circle';
        alertTitle.className = 'text-sm font-semibold text-emerald-800';
        alertText.className = 'text-sm text-emerald-700 opacity-80';
      } else if (kind === 'error') {
        alertBox.classList.add('bg-rose-50', 'border-rose-200');
        alertIcon.className = 'material-symbols-outlined text-rose-600';
        alertIcon.textContent = 'error';
        alertTitle.className = 'text-sm font-semibold text-rose-800';
        alertText.className = 'text-sm text-rose-700 opacity-80';
      } else {
        alertBox.classList.add('bg-blue-50', 'border-blue-200');
        alertIcon.className = 'material-symbols-outlined text-blue-600';
        alertIcon.textContent = 'info';
        alertTitle.className = 'text-sm font-semibold text-blue-800';
        alertText.className = 'text-sm text-blue-700 opacity-80';
      }

      alertTitle.textContent = title;
      alertText.textContent = text;
    };

    const setSaving = (saving: boolean) => {
      saveBtn.disabled = saving;
      saveNextBtn.disabled = saving;
      if (saving) {
        saveBtn.classList.add('opacity-60', 'cursor-not-allowed');
        saveNextBtn.classList.add('opacity-60', 'cursor-not-allowed');
      } else {
        saveBtn.classList.remove('opacity-60', 'cursor-not-allowed');
        saveNextBtn.classList.remove('opacity-60', 'cursor-not-allowed');
      }
    };

    const fillCountries = () => {
      countrySelect.innerHTML = '<option value="">Select country</option>';
      for (const item of countries) {
        const option = document.createElement('option');
        option.value = String(item.id);
        option.textContent = item.name;
        countrySelect.append(option);
      }
    };

    const fillRegions = (countryId?: number) => {
      regionSelect.innerHTML = '<option value="">Select region</option>';
      const items = regions.filter((item) => (countryId ? item.country === countryId : true));
      for (const item of items) {
        const option = document.createElement('option');
        option.value = String(item.id);
        option.textContent = item.name;
        regionSelect.append(option);
      }
    };

    const fillCounties = (regionId?: number) => {
      countySelect.innerHTML = '<option value="">Select county</option>';
      const items = counties.filter((item) => (regionId ? item.region === regionId : true));
      for (const item of items) {
        const option = document.createElement('option');
        option.value = String(item.id);
        option.textContent = item.name;
        countySelect.append(option);
      }
    };

    const updateParentVisibility = () => {
      const type = typeSelect.value as DictionaryGroupKey;
      countryWrap.classList.add('hidden');
      regionWrap.classList.add('hidden');
      countyWrap.classList.add('hidden');

      if (type === 'regions') {
        countryWrap.classList.remove('hidden');
      } else if (type === 'counties') {
        regionWrap.classList.remove('hidden');
      } else if (type === 'settlements') {
        countyWrap.classList.remove('hidden');
      }
    };

    const ensureLocationParentsLoaded = async () => {
      if (locationParentsLoaded) return;
      const [countriesPayload, regionsPayload, countiesPayload] = await Promise.all([
        listCountries(),
        listRegions(),
        listCounties(),
      ]);
      if (disposed) return;
      countries = countriesPayload;
      regions = regionsPayload;
      counties = countiesPayload;
      locationParentsLoaded = true;
      fillCountries();
      fillRegions();
      fillCounties();
    };

    const clearForm = () => {
      nameInput.value = '';
      slugInput.value = '';
      orderInput.value = '0';
      activeInput.checked = true;
      countrySelect.value = '';
      regionSelect.value = '';
      countySelect.value = '';
      slugManuallyEdited = false;
      nameInput.focus();
    };

    const validate = (): string | null => {
      const type = typeSelect.value as DictionaryGroupKey;
      if (!nameInput.value.trim()) return 'Entry name is required.';
      if (type === 'regions' && !countrySelect.value) return 'Parent country is required for region.';
      if (type === 'counties' && !regionSelect.value) return 'Parent region is required for county.';
      if (type === 'settlements' && !countySelect.value) return 'Parent county is required for settlement.';
      return null;
    };

    const createByType = async () => {
      const type = typeSelect.value as DictionaryGroupKey;
      const name = nameInput.value.trim();
      const slug = (slugInput.value.trim() || slugify(name)).slice(0, 255);
      const sortOrder = Number(orderInput.value || '0');
      const isActive = activeInput.checked;

      if (type === 'countries') {
        return createCountry({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'regions') {
        return createRegion({
          name,
          slug,
          country: Number(countrySelect.value),
          is_active: isActive,
          sort_order: sortOrder,
        });
      }
      if (type === 'counties') {
        return createCounty({
          name,
          slug,
          region: Number(regionSelect.value),
          is_active: isActive,
          sort_order: sortOrder,
        });
      }
      if (type === 'settlements') {
        return createSettlement({
          name,
          slug,
          county: Number(countySelect.value),
          is_active: isActive,
          sort_order: sortOrder,
        });
      }
      if (type === 'tags') {
        return createTag({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'location-categories') {
        return createLocationCategory({
          name,
          slug,
          is_active: isActive,
          sort_order: sortOrder,
        });
      }
      if (type === 'location-road-types') {
        return createLocationRoadType({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'location-difficulties') {
        return createLocationDifficulty({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'location-best-seasons') {
        return createLocationBestSeason({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'route-categories') {
        return createRouteCategory({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'route-difficulties') {
        return createRouteDifficulty({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'route-tags') {
        return createRouteTag({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'news-categories') {
        return createNewsCategory({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      if (type === 'news-tags') {
        return createNewsTag({ name, slug, is_active: isActive, sort_order: sortOrder });
      }
      return createEventType({ name, slug, is_active: isActive, sort_order: sortOrder });
    };

    const buildPatchPayloadByType = (type: DictionaryGroupKey): Record<string, unknown> => {
      const name = nameInput.value.trim();
      const slug = (slugInput.value.trim() || slugify(name)).slice(0, 255);
      const sortOrder = Number(orderInput.value || '0');
      const isActive = activeInput.checked;

      if (type === 'regions') {
        return { name, slug, is_active: isActive, sort_order: sortOrder, country: Number(countrySelect.value) };
      }
      if (type === 'counties') {
        return { name, slug, is_active: isActive, sort_order: sortOrder, region: Number(regionSelect.value) };
      }
      if (type === 'settlements') {
        return { name, slug, is_active: isActive, sort_order: sortOrder, county: Number(countySelect.value) };
      }
      if (type === 'location-categories') {
        return {
          name,
          slug,
          is_active: isActive,
          sort_order: sortOrder,
        };
      }
      return { name, slug, is_active: isActive, sort_order: sortOrder };
    };

    const submit = async (stayOnPage: boolean) => {
      const validationError = validate();
      if (validationError) {
        showAlert('error', 'Validation Error', validationError);
        return;
      }

      setSaving(true);
      try {
        const type = typeSelect.value as DictionaryGroupKey;
        const created = isEditMode
          ? await patchDictionaryItem(type, editId, buildPatchPayloadByType(type))
          : await createByType();
        if (disposed) return;

        showAlert(
          'success',
          isEditMode ? 'Entry Updated' : 'Entry Created',
          `Dictionary entry \"${created.name}\" has been ${isEditMode ? 'updated' : 'saved'}.`
        );
        if (stayOnPage && !isEditMode) {
          clearForm();
          return;
        }

        navigate(`/dictionaries?group=${encodeURIComponent(typeSelect.value)}`);
      } catch (error) {
        if (disposed) return;
        const message = getApiErrorMessage(
          error,
          isEditMode ? 'Failed to update dictionary entry.' : 'Failed to create dictionary entry.'
        );
        showAlert('error', 'Request Failed', message);
      } finally {
        if (!disposed) setSaving(false);
      }
    };

    const initialGroup = searchParams.get('group');
    const initialModule = searchParams.get('module');
    const initialModuleKey: ModuleKey =
      initialModule === 'routes' || initialModule === 'news' || initialModule === 'events'
        ? initialModule
        : initialGroup && GROUP_KEYS.includes(initialGroup as DictionaryGroupKey)
          ? GROUP_MODULE_MAP[initialGroup as DictionaryGroupKey]
          : 'locations';
    rebuildTypeOptions(
      initialModuleKey,
      initialGroup && GROUP_KEYS.includes(initialGroup as DictionaryGroupKey)
        ? (initialGroup as DictionaryGroupKey)
        : undefined
    );

    if (isEditMode) {
      pageTitle.textContent = 'Edit Dictionary Entry';
      saveBtnLabel.textContent = 'Update Entry';
      saveNextBtn.classList.add('hidden');
      moduleSelect.disabled = true;
      moduleSelect.classList.add('opacity-60', 'cursor-not-allowed');
      typeSelect.disabled = true;
      typeSelect.classList.add('opacity-60', 'cursor-not-allowed');
    }

    const applyItemToForm = (type: DictionaryGroupKey, item: LocationDictionaryItem) => {
      nameInput.value = item.name ?? '';
      slugInput.value = item.slug ?? '';
      slugManuallyEdited = Boolean(item.slug);
      activeInput.checked = item.is_active ?? true;
      orderInput.value = String(item.sort_order ?? 0);

      if (type === 'regions' && item.country) {
        countrySelect.value = String(item.country);
        fillRegions(item.country);
      }
      if (type === 'counties' && item.region) {
        fillCounties(item.region);
        regionSelect.value = String(item.region);
      }
      if (type === 'settlements' && item.county) {
        countySelect.value = String(item.county);
      }
      rebuildTypeOptions(GROUP_MODULE_MAP[type], type);
    };

    const loadParents = async () => {
      try {
        const type = typeSelect.value as DictionaryGroupKey;
        if (GROUP_MODULE_MAP[type] === 'locations') {
          await ensureLocationParentsLoaded();
        }

        if (isEditMode) {
          const current = await getDictionaryItem(type, editId);
          if (disposed) return;
          applyItemToForm(type, current);
        }
      } catch (error) {
        if (disposed) return;
        const message = getApiErrorMessage(error, 'Failed to load parent dictionaries.');
        showAlert('error', 'Load Failed', message);
      }
    };

    const onTypeChange = () => {
      updateParentVisibility();
      const type = typeSelect.value as DictionaryGroupKey;
      moduleSelect.value = GROUP_MODULE_MAP[type];
      if (GROUP_MODULE_MAP[type] === 'locations') {
        void ensureLocationParentsLoaded();
      }
    };

    const onModuleChange = () => {
      const module = moduleSelect.value as ModuleKey;
      rebuildTypeOptions(module);
      updateParentVisibility();
      const type = typeSelect.value as DictionaryGroupKey;
      if (GROUP_MODULE_MAP[type] === 'locations') {
        void ensureLocationParentsLoaded();
      }
    };

    const onNameInput = () => {
      if (!slugManuallyEdited) {
        slugInput.value = slugify(nameInput.value);
      }
    };

    const onSlugInput = () => {
      slugManuallyEdited = Boolean(slugInput.value.trim());
    };

    const onCountryChange = () => {
      const countryId = countrySelect.value ? Number(countrySelect.value) : undefined;
      fillRegions(countryId);
      fillCounties();
      regionSelect.value = '';
      countySelect.value = '';
    };

    const onRegionChange = () => {
      const regionId = regionSelect.value ? Number(regionSelect.value) : undefined;
      fillCounties(regionId);
      countySelect.value = '';
    };

    const onSaveClick = () => {
      void submit(false);
    };

    const onSaveNextClick = () => {
      void submit(true);
    };

    const onFormSubmit = (event: SubmitEvent) => {
      event.preventDefault();
      void submit(false);
    };

    typeSelect.addEventListener('change', onTypeChange);
    moduleSelect.addEventListener('change', onModuleChange);
    nameInput.addEventListener('input', onNameInput);
    slugInput.addEventListener('input', onSlugInput);
    countrySelect.addEventListener('change', onCountryChange);
    regionSelect.addEventListener('change', onRegionChange);
    saveBtn.addEventListener('click', onSaveClick);
    saveNextBtn.addEventListener('click', onSaveNextClick);
    form.addEventListener('submit', onFormSubmit);

    updateParentVisibility();
    void loadParents();

    return () => {
      disposed = true;
      typeSelect.removeEventListener('change', onTypeChange);
      moduleSelect.removeEventListener('change', onModuleChange);
      nameInput.removeEventListener('input', onNameInput);
      slugInput.removeEventListener('input', onSlugInput);
      countrySelect.removeEventListener('change', onCountryChange);
      regionSelect.removeEventListener('change', onRegionChange);
      saveBtn.removeEventListener('click', onSaveClick);
      saveNextBtn.removeEventListener('click', onSaveNextClick);
      form.removeEventListener('submit', onFormSubmit);
    };
  }, [navigate, searchParams]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Add Dictionary Entry</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
